import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Checkbox} from "semantic-ui-react";
import {TransportOrder} from "../../../../generated";
import {
    determineDotColorForTransportOrder,
    EtaDateTimeDisplay, formatFreight, formatTransport,
    formatTransportOrderStatus,
    getNextStopByEta
} from "./Formatters";
import {formatDate} from "../../../../format";
import ReactTable from "react-table";

export interface TransportOrderTableProps {
    transportOrders: TransportOrder[],
    transportOrderAmount: number,
    selectedOrderIdsChanged: (newSelectedOrderIds: string[]) => void,
    selectedOrderChanged: (newSelectedOrder: TransportOrder) => void,
    reload: (page: number, take: number, sortBy?: string, sortDirection?: "asc" | "desc") => void,
    initialPageSize: number,
    preSelectedTransportOrderIds: string[],
    allowMultipleSelection?: boolean,
    loading?: boolean
}

export function TransportOrderTable(props: TransportOrderTableProps) {
    const [isLoading, setIsLoading] = useState(props.loading ?? true);

    const [take, setTake] = useState(20);
    const [page, setPage] = useState(0);
    const [selectedTransportOrderIds, setSelectedTransportOrderIds] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [sortBy, setSortBy] = useState('created');
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [highlightRowIndex, setHighlightRowIndex] = useState<number>(null);

    useEffect(() => {
        setIsLoading(props.loading);
    }, [props.loading]);

    useEffect(() => {
        setTake(props.initialPageSize);
        setSelectedTransportOrderIds(props.preSelectedTransportOrderIds);
    }, []);

    // ──────────────────────────────────────────────────────────────────────────
    // Refs werden synchron im Render-Body aktualisiert (kein useEffect nötig).
    // So haben columns/getTdProps NIE veraltete Werte, ohne dass sich
    // ihre Referenz ändert → kein gotoPage(0) in ReactTable v6.
    // ──────────────────────────────────────────────────────────────────────────
    const allSelectedRef = useRef(allSelected);
    allSelectedRef.current = allSelected;

    const selectedTransportOrderIdsRef = useRef(selectedTransportOrderIds);
    selectedTransportOrderIdsRef.current = selectedTransportOrderIds;

    const highlightRowIndexRef = useRef(highlightRowIndex);
    highlightRowIndexRef.current = highlightRowIndex;

    // Callbacks als Refs – zeigen immer auf die aktuelle Funktion
    const switchAllRef = useRef<() => void>(null);
    const switchItemRef = useRef<(id: string) => void>(null);
    const selectedOrderChangedRef = useRef(props.selectedOrderChanged);
    selectedOrderChangedRef.current = props.selectedOrderChanged;

    function switchSelectionStateOfAllItems() {
        if (allSelectedRef.current) {
            const newIds: string[] = [];
            setAllSelected(false);
            setSelectedTransportOrderIds(newIds);
            props.selectedOrderIdsChanged(newIds);
        } else {
            const newIds = props.transportOrders.map(to => to._id);
            setAllSelected(true);
            setSelectedTransportOrderIds(newIds);
            props.selectedOrderIdsChanged(newIds);
        }
    }
    switchAllRef.current = switchSelectionStateOfAllItems;

    function switchSelectionStateOfItem(id: string) {
        setIsLoading(true);

        let array: string[] = [];
        if (props.allowMultipleSelection) {
            array = [...selectedTransportOrderIdsRef.current];
        }
        const index = array.indexOf(id);
        if (index === -1) {
            const newArray = array.concat(id);
            setSelectedTransportOrderIds(newArray);
            props.selectedOrderIdsChanged(newArray);
        } else {
            array.splice(index, 1);
            setSelectedTransportOrderIds(array);
            props.selectedOrderIdsChanged(array);
        }

        const newLength = index === -1
            ? selectedTransportOrderIdsRef.current.length + 1
            : selectedTransportOrderIdsRef.current.length - 1;
        setAllSelected(newLength === props.transportOrders.length);

        setIsLoading(false);
    }
    switchItemRef.current = switchSelectionStateOfItem;

    function changePage(pageIndex: number) {
        if (pageIndex === page) return;
        console.log("changePage: pageIndex : ", pageIndex);
        setPage(pageIndex);
        setIsLoading(true);
        props.reload(pageIndex, take, sortBy, sortDirection);
    }

    function changePageSize(newSize: number) {
        if (newSize === take) return;
        console.log("changePageSize newSize : ", newSize);
        setTake(newSize);
        setIsLoading(true);
        props.reload(page, newSize, sortBy, sortDirection);
    }

    function changeSort(newSortBy: string, newSortDirection: "asc" | "desc") {
        const direction = newSortDirection.toLowerCase() as "asc" | "desc";
        if (newSortBy === sortBy && direction === sortDirection) return;
        console.log("changeSort:", newSortBy, newSortDirection);
        setSortBy(newSortBy);
        setSortDirection(direction);
        setIsLoading(true);
        props.reload(page, take, newSortBy, direction);
    }

    // ──────────────────────────────────────────────────────────────────────────
    // columns: NUR von props.allowMultipleSelection abhängig → stabile Referenz.
    // Alle Laufzeit-Werte kommen aus Refs → immer aktuell, ohne deps zu ändern.
    // ──────────────────────────────────────────────────────────────────────────
    const columns = useMemo(() => [
        {
            id: 'select',
            Header: () => props.allowMultipleSelection
                ? <Checkbox
                    checked={allSelectedRef.current}
                    onChange={() => switchAllRef.current?.()}
                  />
                : <span></span>,
            width: 30,
            sortable: false,
            Cell: (row: any) => (
                <Checkbox
                    checked={selectedTransportOrderIdsRef.current.some(id => id === row.original._id)}
                    onChange={() => switchItemRef.current?.(row.original._id)}
                />
            ),
            accessor: () => null,
        }, {
            id: '_id',
            Header: 'Id',
            accessor: (d: TransportOrder) => d._id
        }, {
            id: 'status',
            Header: 'Status',
            accessor: (d: TransportOrder) => formatTransportOrderStatus(d.status),
        },
        {
            id: 'externalTransportOrderId',
            Header: 'ExternalOrderNo',
            accessor: (d: TransportOrder) => d.externalTransportOrderId,
            Cell: (props: any) => <span className='number'>{props.value}</span>
        }, {
            id: 'created',
            Header: 'Created',
            width: 140,
            accessor: (d: TransportOrder) => formatDate(d.created)
        },
        {
            id: 'planStart',
            Header: 'PlanStart',
            width: 140,
            accessor: (d: TransportOrder) => formatDate(d.planStart),
        },
        {
            id: 'planEnd',
            Header: 'PlanEnd',
            width: 140,
            accessor: (d: TransportOrder) => formatDate(d.planEnd),
        },
        {
            id: 'targetCompanyName',
            Header: 'Contractor',
            accessor: (d: TransportOrder) => d.targetCompanyName
        },
        {
            id: 'is_subchartered',
            Header: "Via Sub?",
            sortable: false,
            accessor: (d: TransportOrder) => d.linkInfo != null && d.linkInfo.targetTransportOrderId != null ? "☑" : ""
        },
        {
            id: 'observer',
            sortable: false,
            Header: 'Observer',
            accessor: (d: TransportOrder) => d.observer && d.observer.length > 0 ? "☑ " + d.observer.length : ""
        },
        {
            id: 'original_source_company',
            sortable: false,
            Header: 'Consignor',
            accessor: (d: TransportOrder) => d.linkInfo != null ? d.linkInfo.sourceCompanyName : ""
        },
        {
            id: 'companyName',
            Header: 'Client',
            accessor: (d: TransportOrder) => d.companyName
        },
        {
            id: 'vehicle',
            Header: 'Vehicle',
            accessor: (d: TransportOrder) => {
                if (d.assignedVehicle != null && d.assignedVehicle.numberPlate != null) {
                    return d.assignedVehicle.numberPlate
                } else if (d.assignedDevice != null && d.assignedDevice.verboseName != null) {
                    return d.assignedDevice.verboseName
                }
                return ""
            }
        },
        {
            id: 'driver',
            Header: 'Driver',
            accessor: (d: TransportOrder) => {
                if (d.assignedDriver != null && (d.assignedDriver.firstName != null || d.assignedDriver.lastName != null)) {
                    return `${d.assignedDriver.firstName} ${d.assignedDriver.lastName}`
                } else if (d.assignedDriver != null && d.assignedDriver.externalDriverId != null) {
                    return d.assignedDriver.externalDriverId
                }
                return ""
            }
        },
        {
            id: 'trailer',
            Header: 'Trailer',
            accessor: (d: TransportOrder) => {
                if (d.assignedTrailer?.numberPlate != null) {
                    return d.assignedTrailer.numberPlate
                } else if (d.assignedTrailer?.externalVehicleId != null) {
                    return d.assignedTrailer.externalVehicleId
                }
                return ""
            }
        },
        {
            id: 'eta',
            Header: 'NextETA',
            width: 135,
            accessor: (d: TransportOrder) => {
                const nextEta = getNextStopByEta(d);
                if (nextEta?.eta) {
                    return <EtaDateTimeDisplay eta={nextEta.eta}/>
                }
                return ""
            },
            Cell: (row: any) => (
                <span>
                    <span style={{
                        color: determineDotColorForTransportOrder(row.original),
                        transition: 'all .3s ease'
                    }}>&#x25cf;</span>
                    {row.value}
                </span>
            )
        },
        {
            id: 'from',
            Header: 'Stops',
            accessor: (d: TransportOrder) => d.transports != null && formatTransport(d.transports)
        },
        {
            id: 'freight',
            Header: 'Freight',
            accessor: (d: TransportOrder) => d.freight != null && formatFreight(d.freight)
        },
        {
            id: 'assignedDeviceMail',
            visible: false,
            Header: 'AssignedDeviceMail',
            accessor: (d: TransportOrder) => d.assignedDevice?.emailAddress
        },
        {
            id: 'assignedDevicePhone',
            visible: false,
            Header: 'AssignedDevicePhone',
            accessor: (d: TransportOrder) => d.assignedDevice?.phoneNr
        }
    // Nur allowMultipleSelection als Dep – nichts Selektions-Abhängiges!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [props.allowMultipleSelection]);

    const sortedProp = useMemo(
        () => [{ id: sortBy, desc: sortDirection === "desc" }],
        [sortBy, sortDirection]
    );

    // getTdProps ebenfalls stabil – liest highlightRowIndex aus Ref
    const getTdProps = useCallback((state: any, rowInfo: any, column: any, instance: any) => {
        return {
            onClick: () => {
                if (rowInfo) {
                    console.log("Opening TransportOrder with id", rowInfo.row);
                    setHighlightRowIndex(rowInfo.index);
                    selectedOrderChangedRef.current(rowInfo.row._original);
                }
            },
            style: {
                background: rowInfo?.index === highlightRowIndexRef.current ? '#00afec' : 'white',
                color: rowInfo?.index === highlightRowIndexRef.current ? 'white' : 'black'
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <ReactTable
        className="transportOrderTable -striped -highlight"
        data={props.transportOrders}
        pages={Math.ceil(props.transportOrderAmount / take)}
        page={page}
        manual
        columns={columns}
        sorted={sortedProp}
        onSortedChange={(newSorted, column, shiftKey) => {
            if (newSorted && newSorted.length > 0) {
                const sortInfo = newSorted[0];
                changeSort(sortInfo.id, sortInfo.desc ? "desc" : "asc");
            }
        }}
        getTdProps={getTdProps}
        defaultPageSize={props.initialPageSize}
        pageSizeOptions={[5, 25, 50, 100, 250, 500, 1000, 2500, 5000]}
        loading={isLoading}
        style={{cursor: "pointer"}}
        onPageChange={(pageIndex) => changePage(pageIndex)}
        onPageSizeChange={(pageSize, pageIndex) => changePageSize(pageSize)}
    />
}
